import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { t } from '@/i18n';
import type { AgingBuckets, FinancialOverview } from '@ip/shared-types';
import { formatPLN, formatPct } from './chart-utils';
import { KpiTile } from './KpiTile';

interface FinancialKpiTilesProps {
  kpis: FinancialOverview['kpis'];
  receivables: AgingBuckets;
  payables: AgingBuckets;
  hasSales: boolean;
}

function overdueTotal(buckets: AgingBuckets): number {
  return buckets.d30 + buckets.d60 + buckets.d90 + buckets.older;
}

function MoMChip({ value }: { value: number }) {
  const up = value >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex w-fit items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums ${
        up
          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
          : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
      }`}
      title={t('dashboard.financial.momTitle')}
    >
      <Icon aria-hidden="true" className="size-3" />
      {t('dashboard.financial.mom', { pct: formatPct(Math.abs(value)) })}
    </span>
  );
}

function OverdueLine({ buckets }: { buckets: AgingBuckets }) {
  const overdue = overdueTotal(buckets);
  if (overdue <= 0) {
    return <span className="text-muted-foreground">{t('dashboard.financial.allOnTime')}</span>;
  }
  return (
    <span className="block truncate text-amber-600 dark:text-amber-500">
      {t('dashboard.financial.overdue', { amount: formatPLN(overdue) })}
    </span>
  );
}

export function FinancialKpiTiles({ kpis, receivables, payables, hasSales }: FinancialKpiTilesProps) {
  const vatState =
    kpis.vatPosition > 0 ? 'payable' : kpis.vatPosition < 0 ? 'refund' : 'neutral';
  const vatLabel =
    vatState === 'payable'
      ? t('dashboard.financial.vatPayable')
      : vatState === 'refund'
        ? t('dashboard.financial.vatRefund')
        : t('dashboard.financial.vatNeutral');
  const vatClass =
    vatState === 'payable'
      ? 'text-amber-600 dark:text-amber-500'
      : vatState === 'refund'
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-muted-foreground';

  const tiles: ReactNode[] = [];

  if (hasSales) {
    tiles.push(
      <KpiTile
        key="revenue"
        emphasis="hero"
        caption={t('dashboard.financial.revenueYtd')}
        value={kpis.revenueYtd}
        secondary={kpis.revenueMoM != null ? <MoMChip value={kpis.revenueMoM} /> : undefined}
      />,
    );
  }
  tiles.push(<KpiTile key="cost" emphasis="hero" caption={t('dashboard.financial.costYtd')} value={kpis.costYtd} />);
  if (hasSales) {
    tiles.push(
      <KpiTile
        key="margin"
        emphasis="hero"
        caption={t('dashboard.financial.margin')}
        value={kpis.marginYtd}
        secondary={
          kpis.marginPct != null ? (
            <span className="text-muted-foreground">
              {t('dashboard.financial.marginOfRevenue', { pct: formatPct(kpis.marginPct) })}
            </span>
          ) : undefined
        }
      />,
    );
  }
  tiles.push(
    <KpiTile
      key="vat"
      emphasis="hero"
      caption={t('dashboard.financial.vatPosition')}
      value={Math.abs(kpis.vatPosition)}
      secondary={<span className={`font-medium ${vatClass}`}>{vatLabel}</span>}
    />,
  );
  if (hasSales || kpis.receivablesTotal !== 0) {
    tiles.push(
      <KpiTile
        key="receivables"
        emphasis="hero"
        caption={t('dashboard.financial.receivables')}
        value={kpis.receivablesTotal}
        secondary={<OverdueLine buckets={receivables} />}
      />,
    );
  }
  tiles.push(
    <KpiTile
      key="payables"
      emphasis="hero"
      caption={t('dashboard.financial.payables')}
      value={kpis.payablesTotal}
      secondary={<OverdueLine buckets={payables} />}
    />,
  );

  const gridCols =
    tiles.length >= 5 ? 'md:grid-cols-3 xl:grid-cols-6' : 'md:grid-cols-3 xl:grid-cols-3';

  return <div className={`grid grid-cols-2 gap-3 ${gridCols}`}>{tiles}</div>;
}
